/**
 * ページ全体のレイアウトラッパー。header（タイトル + stepper）と main を整える。
 *
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business voice-enrollment Web のページ共通フレーム
 * @graph-connects none
 */

import type { ReactNode } from 'react';
import { Stepper, type StepperStep } from './Stepper.js';

interface LayoutProps {
  appTitle: string;
  steps: readonly StepperStep[];
  currentStepIndex: number;
  children: ReactNode;
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business 全画面共通のフレーム。タイトル + stepper を上部に固定し、内側に画面コンテンツ
 * @graph-connects none
 */
export function Layout({ appTitle, steps, currentStepIndex, children }: LayoutProps): ReactNode {
  return (
    <div className="relative min-h-screen min-h-[100dvh] px-4 py-8 sm:px-6 sm:py-12">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-8 sm:gap-10">
        <header className="flex flex-col gap-6">
          <div className="flex items-center gap-2.5">
            <span
              aria-hidden
              className="inline-flex h-7 w-7 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-accent-soft)] text-[var(--color-accent)]"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="9" y="3" width="6" height="12" rx="3" />
                <path d="M5 11a7 7 0 0 0 14 0" />
                <line x1="12" y1="18" x2="12" y2="22" />
              </svg>
            </span>
            <h1 className="text-base font-semibold tracking-tight text-[var(--color-text-primary)]">
              {appTitle}
            </h1>
          </div>
          {currentStepIndex >= 0 ? <Stepper steps={steps} currentIndex={currentStepIndex} /> : null}
        </header>
        <main style={{ animation: 'fade-in-up var(--duration-slow) var(--ease-out)' }}>
          {children}
        </main>
      </div>
    </div>
  );
}
